import React, { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { StyledModal } from "components/shared";
import { useCountryValue, selectCountry } from "stores/country";
import { getFieldValue } from "@sitecore-jss/sitecore-jss-react";
import Button from "components/components/atoms/Button";
import useBreakpoint from "utils/hooks/useBreakpoint";
import breakpoints from "utils/breakpoints";
import cx from "classnames";
import getNearestCountry from "utils/getNearestCountry";
import { withSitecoreContext } from "@sitecore-jss/sitecore-jss-react";
import { isValidCountry, getCountryAppCode } from "utils/geolocation";
import { useLocation, useHistory } from "react-router-dom";
import queryString from "query-string";

const ModalContent = styled.div`
  width: 20.625rem;
  border-radius: 0.625rem;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.4);
  @media ${breakpoints.md} {
    width: 36.25rem;
  }
`;

const Heading = styled.div`
  max-width: 15rem;
  padding-top: 2.85rem;
  letter-spacing: -0.5px;
  line-height: 2.625rem;
  @media ${breakpoints.md} {
    max-width: 26rem;
    padding-top: 4.8rem;
  }
`;

const Text = styled.div`
  max-width: 16.938rem;
  letter-spacing: 0.11px;
  @media ${breakpoints.md} {
    max-width: 29.938rem;
  }
`;
const ButtonsContainer = styled.div`
  padding-bottom: 1.75rem;
  @media ${breakpoints.md} {
    margin-top: 1.45rem;
    padding-bottom: 2.75rem;
  }
`;
const CountryButton = styled(Button)`
  @media ${breakpoints.md} {
    width: 10rem;
  }
`;

const CountrySelector = ({ fields, sitecoreContext }) => {
  const titleCountrySelector = getFieldValue(fields, "titleCountrySelector");
  const descriptionCountrySelector = getFieldValue(
    fields,
    "descriptionCountrySelector"
  );
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);
  const [{ country }, dispatch] = useCountryValue();
  const location = useLocation();
  const history = useHistory();
  const params = queryString.parse(location.search);

  const handleClick = useCallback((value) => {
    if (typeof window !== "undefined" && localStorage) {
      localStorage.setItem("userCountry", value);
      dispatch(selectCountry(value));
      if (location.pathname === "/") {
        const newParams = { country: value };
        history.push({
          pathname: location.pathname,
          search: "?" + new URLSearchParams(newParams).toString(),
          hash: location.hash,
        });
      }
    }
  });

  let savedCountry;
  if (typeof window !== "undefined" && localStorage) {
    savedCountry = localStorage.getItem("userCountry");
    if (params && params.country && ["CAN", "USA"].includes(params.country)) {
      savedCountry = params.country;
    }
    if (!savedCountry) {
      const geolocation = getFieldValue(sitecoreContext, "geolocation") || {};

      if (geolocation && geolocation.country) {
        let countryCode = geolocation.country;

        if (isValidCountry(countryCode)) {
          const countryAppCode = getCountryAppCode(countryCode);

          if (typeof window !== "undefined" && localStorage) {
            localStorage.setItem("userCountry", countryAppCode);
            savedCountry = countryAppCode;
            if (country !== savedCountry) dispatch(selectCountry(savedCountry));
          }
        } else {
          let nearestCountry = getNearestCountry(
            geolocation.latitude,
            geolocation.longitude
          );
          localStorage.setItem("userCountry", nearestCountry);
          savedCountry = nearestCountry;
          if (country !== savedCountry) dispatch(selectCountry(savedCountry));
        }
      } else {
        console.log("Show Country Selector Modal");
      }
    } else {
      if (country !== savedCountry) {
        dispatch(selectCountry(savedCountry));
      }
    }
  }

  const titleCountrySelectorRef = useRef();

  useEffect(() => {
    setTimeout(() => {
      if (titleCountrySelectorRef.current) {
        const element = titleCountrySelectorRef.current;
        element.children[0].focus();
      }
    }, 2000);
  }, [titleCountrySelectorRef]);

  return savedCountry && ["CAN", "USA"].includes(savedCountry) ? (
    ""
  ) : (
    <StyledModal isOpen={!["USA", "CAN"].includes(country)}>
      <div
        className="flex justify-center items-center bg-black-faded w-screen h-screen"
        aria-modal="true"
        role="dialog"
      >
        <ModalContent className="flex flex-col items-center bg-white">
          <Heading className="w-full font-trade-gothic-20 text-mattamy-blue text-4xl text-center">
            {titleCountrySelector}
          </Heading>

          <Text className="w-full font-graphie text-mattamy-blue text-base text-center leading-7 mt-3 md:mt-0">
            {descriptionCountrySelector}
          </Text>
          <ButtonsContainer
            className={cx("items-center", {
              "flex flex-col mt-8 w-full px-6": isMobile,
            })}
            ref={titleCountrySelectorRef}
          >
            <CountryButton
              color={"action-blue"}
              variant={"outlined"}
              className="text-base h-60-mattamy pt-0 pb-0 mb-5 md:mr-2 md:ml-3 hover:text-white hover:bg-action-blue"
              onClick={() => handleClick("CAN")}
              tabIndex={0}
            >
              CANADA
            </CountryButton>
            <CountryButton
              color={"action-blue"}
              variant={"outlined"}
              className="text-base h-60-mattamy pt-0 pb-0 mb-5 md:mr-2 md:ml-3 hover:text-white hover:bg-action-blue"
              onClick={() => handleClick("USA")}
              tabIndex={0}
            >
              USA
            </CountryButton>
          </ButtonsContainer>
        </ModalContent>
      </div>
    </StyledModal>
  );
};

export default withSitecoreContext({ updatable: true })(CountrySelector);
